import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Download, Upload } from "lucide-react";
import { api } from "../lib/api";
import { isDone } from "../components/FollowUpSlot";
import { STAGES, SOURCES, PRIORITIES, INDIAN_STATES, INTERESTS } from "../lib/constants";
import { PageHeader, Card } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Sel } from "../components/Sel";
import { StageBadge, PriorityBadge } from "../components/Badges";
import { LeadFormDialog } from "../components/LeadFormDialog";
import { LeadDrawer } from "../components/LeadDrawer";
import { ImportLeadsDialog } from "../components/ImportLeadsDialog";

const ALL = { value: "all", label: "All" };

const exportCsv = (rows) => {
  const cols = ["name", "company", "email", "phone", "city", "state", "interested_for", "source", "service", "status", "priority", "assigned_name", "created_at"];
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "greenrgy-leads.csv";
  a.click();
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [priority, setPriority] = useState("all");
  const [stateF, setStateF] = useState("all");
  const [interest, setInterest] = useState("all");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [imp, setImp] = useState(false);

  const load = useCallback(() => {
    const params = { q };
    if (status !== "all") params.status = status;
    if (source !== "all") params.source = source;
    if (priority !== "all") params.priority = priority;
    if (stateF !== "all") params.state = stateF;
    if (interest !== "all") params.interested_for = interest;
    api.get("/leads", { params }).then((r) => setLeads(r.data));
  }, [q, status, source, priority, stateF, interest]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="leads-page">
      <PageHeader title="Leads" subtitle={`${leads.length} leads in view`}>
        <Button variant="outline" onClick={() => setImp(true)} data-testid="import-leads-btn"><Upload className="h-4 w-4 mr-1" />Import</Button>
        <Button variant="outline" onClick={() => exportCsv(leads)} data-testid="export-leads-btn"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="add-lead-btn"><Plus className="h-4 w-4 mr-1" />New lead</Button>
      </PageHeader>
      <Card className="p-4 mb-4 flex flex-col lg:flex-row lg:flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="leads-search-input" className="pl-9" placeholder="Search name, company, phone, email, city..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Sel testid="filter-status" className="lg:w-40" value={status} onChange={setStatus} options={[{ ...ALL, label: "All stages" }, ...STAGES.map((s) => ({ value: s.key, label: s.label }))]} />
        <Sel testid="filter-source" className="lg:w-40" value={source} onChange={setSource} options={[{ ...ALL, label: "All sources" }, ...SOURCES]} />
        <Sel testid="filter-state" className="lg:w-44" value={stateF} onChange={setStateF} options={[{ ...ALL, label: "All states" }, ...INDIAN_STATES]} />
        <Sel testid="filter-interest" className="lg:w-44" value={interest} onChange={setInterest} options={[{ ...ALL, label: "All interests" }, ...INTERESTS]} />
        <Sel testid="filter-priority" className="lg:w-40" value={priority} onChange={setPriority} options={[{ ...ALL, label: "All priorities" }, ...PRIORITIES]} />
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
              {["Lead", "State", "Interested For", "Stage", "Priority", "Follow-ups", "Phone No"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => {
              const done = l.followups.filter(isDone).length;
              return (
                <tr key={l.id} onClick={() => setSel(l.id)} data-testid={`lead-row-${l.id}`} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50/80 transition-colors">
                  <td className="px-4 py-3"><div className="font-semibold">{l.name}</div><div className="text-xs text-slate-500">{l.company} {l.city && `· ${l.city}`}</div></td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.state || "-"}</td>
                  <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">{l.interested_for || "-"}</td>
                  <td className="px-4 py-3"><StageBadge status={l.status} /></td>
                  <td className="px-4 py-3"><PriorityBadge p={l.priority} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">{l.followups.map((f) => <span key={f.no} className={`h-2 w-5 rounded-full ${isDone(f) ? "bg-emerald-500" : f.date ? "bg-amber-300" : "bg-slate-200"}`} />)}</div>
                    <div className="mt-1 text-xs text-slate-500">{done}/{l.followups.length} done</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-mono text-xs" data-testid={`lead-phone-${l.id}`}>{l.phone ? <a href={`tel:${l.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-emerald-700">{l.phone}</a> : "-"}</td>
                </tr>
              );
            })}
            {!leads.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500" data-testid="leads-empty">No leads found. Create your first lead.</td></tr>}
          </tbody>
        </table>
      </Card>
      <LeadFormDialog open={open} onOpenChange={setOpen} onSaved={load} />
      <ImportLeadsDialog open={imp} onOpenChange={setImp} onDone={load} />
      <LeadDrawer leadId={sel} onClose={() => setSel(null)} onChanged={load} />
    </div>
  );
}
